import {
  CheckCircle2,
  FileText,
  Send,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  formatEvaluationAmount,
} from "../../model";
import type {
  useOffersWorkspace,
} from "./useOffersWorkspace";

export function OfferDecision({ model }: { model: ReturnType<typeof useOffersWorkspace>; }) {
  const {
    evaluation,
    activeRequestNo,
    recommendationOfferId,
    setRecommendationOfferId,
    recommendationRationale,
    setRecommendationRationale,
    decisionMode,
    setDecisionMode,
    decisionEditorOpen,
    setDecisionEditorOpen,
    routingForApproval,
    submitDecisionToManager,
    activeRequest,
    decisionCanBeSubmitted,
    systemOffer,
    decisionOffers,
    systemRecommendationSummary,
    singleOffer,
    selectedDecisionOffer,
  } = model;
  return (<>
    {decisionEditorOpen && evaluation ? (
      <section
        className="form-modal-surface procurement-decision-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="procurement-decision-title"
      >
        <div className="section-heading compact">
          <div>
            <span className="section-kicker">Uzman kararı</span>
            <h2 id="procurement-decision-title">
              Teklifi seç ve Müdüre ilet
            </h2>
            <p className="modal-description">
              Seçiminiz, gerekçeniz ve değerlendirme sürümü denetim kaydına
              işlenir.
            </p>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Karar penceresini kapat"
            disabled={routingForApproval}
            onClick={() => setDecisionEditorOpen(false)}
          >
            <X size={17} />
          </button>
        </div>
        <div className="procurement-decision-body">
          <div className="decision-request-context">
            <div>
              <small>Satın alma talebi</small>
              <strong>{activeRequest?.title ?? activeRequestNo}</strong>
              <span>{activeRequestNo}</span>
            </div>
            <span>Sürüm {evaluation.version}</span>
          </div>

          {evaluation.status === "COMPLETED" ? (
            <fieldset className="decision-choice-group">
              <legend>İlerlenecek teklif</legend>
              {decisionOffers.map((offer) => {
                const isSystemOffer = offer.id === evaluation.topOfferId;
                const isSelected =
                  recommendationOfferId === offer.id &&
                  (isSystemOffer
                    ? decisionMode === "SYSTEM"
                    : decisionMode === "ALTERNATIVE");
                return (
                  <button
                    key={offer.id}
                    type="button"
                    className={isSelected ? "is-selected" : ""}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setDecisionMode(
                        isSystemOffer ? "SYSTEM" : "ALTERNATIVE",
                      );
                      setRecommendationOfferId(offer.id);
                    }}
                  >
                    <span className="decision-offer-icon" aria-hidden="true">
                      {isSystemOffer ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <FileText size={18} />
                      )}
                    </span>
                    <span className="decision-offer-content">
                      <span className="decision-offer-heading">
                        <strong>{offer.supplier}</strong>
                        {isSystemOffer ? <em>Sistem önerisi</em> : null}
                      </span>
                      <span className="decision-offer-metrics">
                        <span>
                          <small>Puan</small>
                          <strong>
                            {offer.totalScore?.toLocaleString("tr-TR")}
                          </strong>
                        </span>
                        <span>
                          <small>Toplam maliyet</small>
                          <strong>
                            {formatEvaluationAmount(
                              offer.comparableTotal ??
                              offer.enteredTotal ??
                              offer.amount,
                              evaluation.comparisonCurrency,
                            )}
                          </strong>
                        </span>
                        <span>
                          <small>Teslimat</small>
                          <strong>
                            {offer.deliveryDays === null
                              ? "—"
                              : `${offer.deliveryDays} gün`}
                          </strong>
                        </span>
                        <span>
                          <small>Ödeme vadesi</small>
                          <strong>
                            {offer.paymentDays === null
                              ? "—"
                              : `${offer.paymentDays} gün`}
                          </strong>
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
              {systemOffer ? (
                <div className="system-recommendation-reason" role="note">
                  <ShieldCheck size={17} aria-hidden="true" />
                  <div>
                    <strong>Sistem neden bunu öneriyor?</strong>
                    <p>{systemRecommendationSummary}</p>
                  </div>
                </div>
              ) : null}
            </fieldset>
          ) : (
            <div className="single-source-decision">
              <FileText size={18} />
              <div>
                <small>Tek geçerli teklif</small>
                <strong>{singleOffer?.supplier}</strong>
                <span>{singleOffer?.offerNo}</span>
              </div>
            </div>
          )}

          {selectedDecisionOffer ? (
            <div className="selected-offer-facts">
              <div>
                <small>Toplam maliyet</small>
                <strong>
                  {formatEvaluationAmount(
                    selectedDecisionOffer.comparableTotal ??
                    selectedDecisionOffer.enteredTotal ??
                    selectedDecisionOffer.amount,
                    evaluation.comparisonCurrency,
                  )}
                </strong>
              </div>
              <div>
                <small>Teslimat</small>
                <strong>
                  {selectedDecisionOffer.deliveryDays === null
                    ? "—"
                    : `${selectedDecisionOffer.deliveryDays} gün`}
                </strong>
              </div>
              <div>
                <small>Ödeme vadesi</small>
                <strong>
                  {selectedDecisionOffer.paymentDays === null
                    ? "—"
                    : `${selectedDecisionOffer.paymentDays} gün`}
                </strong>
              </div>
              <div>
                <small>Teklif belgesi</small>
                <strong>
                  {selectedDecisionOffer.documentPresent ? "Var" : "Eksik"}
                </strong>
              </div>
            </div>
          ) : null}

          <label className="decision-rationale-field">
            Uzman önerisi gerekçesi
            <textarea
              rows={4}
              value={recommendationRationale}
              onChange={(event) =>
                setRecommendationRationale(event.target.value)
              }
              placeholder="Seçimin fiyat, teslimat, vade ve operasyonel etkisini açıklayın"
            />
            <small>
              Sistem önerisi seçilse bile karar sorumluluğu uzmana aittir. En
              az 10 karakterlik somut gerekçe yazın.
            </small>
          </label>
        </div>
        <div className="procurement-decision-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={routingForApproval}
            onClick={() => setDecisionEditorOpen(false)}
          >
            Vazgeç
          </button>
          <button
            type="button"
            className="approve-button"
            disabled={!decisionCanBeSubmitted || routingForApproval}
            onClick={submitDecisionToManager}
          >
            <Send size={16} />
            {routingForApproval
              ? "Kaydediliyor ve iletiliyor…"
              : "Onaya gönder"}
          </button>
        </div>
      </section>
    ) : null}
  </>);
}
